<script setup>
  import { Dialog } from '@headlessui/vue'
  import { notify } from 'notiwind'
  import { handleError } from '@/utils/error'

  const props = defineProps({
    job: { type: Object },
    employer: { type: Object },
    mandatoryLetter: { type: Boolean },
    canLetterQuestions: { type: Boolean },
    motivationLetterField: { type: Boolean, default: true },
    messageField: { type: Boolean, default: false },
    otherFileField: { type: Boolean, default: false }
  })

  const emit = defineEmits(['applied'])

  const client = useStrapiClient()
  const route = useRoute()
  const { user } = useUser()
  const localForage = useLocalForage()

  const Steps = {
    CV: 'cv',
    EXPERIENCE: 'experience'
  }
  const step = ref(Steps.CV)
  const isOpen = ref(false)

  watch(
    () => route.hash,
    value => {
      if (user.value) {
        if (!user.value.candidate) return
        if (props.job && user.value.hasApplied(props.job.id)) return
      }
      if (value === '#postuler') {
        step.value = Steps.CV
        open()
      } else if (value === '#experience') {
        step.value = Steps.EXPERIENCE
        open()
      }
    }
  )

  const storeData = async (data, values) => {
    localStorage.setItem('applicationData', JSON.stringify(data))
    await Promise.all([
      localForage.setItem('applicationCv', values.cv),
      localForage.setItem('applicationLetter', values.letter),
      localForage.setItem('applicationOtherFile', values.otherFile)
    ])
  }

  const retrieveData = async () => {
    const [cv, letter, otherFile] = await Promise.all([
      localForage.getItem('applicationCv'),
      localForage.getItem('applicationLetter'),
      localForage.getItem('applicationOtherFile')
    ])
    const data = JSON.parse(localStorage.getItem('applicationData'))
    return [cv, letter, otherFile, data]
  }

  const buildFormData = (cv, letter, otherFile, data) => {
    const formData = new FormData()
    if (cv) formData.append('files.cv', cv)
    if (letter) formData.append('files.letter', letter)
    if (otherFile) formData.append('files.otherFile', otherFile)
    formData.append('data', JSON.stringify(data))
    return formData
  }

  const finishSubmit = async formData => {
    try {
      await client('/applications', { method: 'POST', body: formData })
      close()
      notify({ group: 'main', type: 'success', title: 'Candidature envoyée' })
      emit('applied')
    } catch (error) {
      handleError(error, true)
    }
  }

  const handleOrigin = query => {
    let { o: origin, xsource } = query
    if (origin === 'xm' && xsource) return xsource
    return (
      {
        ap: 'apec',
        et: 'emploi-territorial',
        ft: 'france-travail',
        go: 'google',
        in: 'indeed',
        pp: 'pp',
        ro: 'robopost',
        talent: 'talent',
        ta: 'talent',
        pc: 'profil-culture'
      }[origin] ?? null
    )
  }

  const experienceSubmit = async () => {
    const [cv, letter, otherFile, data] = await retrieveData()
    const formData = buildFormData(cv, letter, otherFile, data)
    finishSubmit(formData)
  }

  const cvSubmit = async values => {
    try {
      const j = props.job
      const emp = props.employer

      const data = {
        message: values.message,
        origin: handleOrigin(route.query),
        originUrl: sessionStorage.getItem(j ? `job:${j.id}` : `employer:${emp.id}`)
      }
      if (j) data.job = j.id
      if (emp) data.employer = emp.id

      await storeData(data, values)
      // An user can start the process without being logged in, in this case we login the user,
      // then finish the process after the redirect, see the watch in this script for the follow up.
      if (!user.value) {
        close()
        askLogin(route.path + '#experience')
      } else {
        step.value = Steps.EXPERIENCE
      }
    } catch (error) {
      handleError(error, true)
    }
  }

  const open = () => {
    isOpen.value = true
  }
  const close = () => (isOpen.value = false)
  defineExpose({ open })
</script>

<template>
  <ClientOnly>
    <Dialog
      class="fixed inset-0 z-20 flex h-full w-full justify-center overflow-auto bg-black bg-opacity-50 p-4 py-24 font-main text-tarawera-900 backdrop-blur"
      id="postuler"
      :open="isOpen"
    >
      <ModalPanelCvLetter
        v-if="step === Steps.CV"
        :close="close"
        :onSubmit="cvSubmit"
        :mandatoryLetter="props.mandatoryLetter"
        :canLetterQuestions="props.canLetterQuestions"
        :motivationLetterField="props.motivationLetterField"
        :messageField="props.messageField"
        :otherFileField="props.otherFileField"
        :job="props.job"
      />
      <ModalPanelCompleteExperiences v-else-if="step === Steps.EXPERIENCE" :optional="!!props.job" :close="close" :onSubmit="experienceSubmit" />
    </Dialog>
  </ClientOnly>
</template>
